import { useQuery } from '@tanstack/react-query'
import { Info, Loader2, Network, Play } from 'lucide-react'
import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { PlaygroundAttempt, PlaygroundEntity } from '@/services/api/playground'
import { schemasApi } from '@/services/api/schemas'
import { DemoEntityGroupCard, entityName } from './DemoEntityGroupCard'
import { GraphPreview } from './GraphPreview'
import { StepStrip, type StepView } from './StepStrip'

interface CaseSectionProps {
  attempts: PlaygroundAttempt[]
  attemptCount: number
  maxAttempts: number
  running: boolean
  steps: StepView[]
  error: string | null
  selectedSchemaId: string | undefined
  onSelectSchema: (schemaId: string) => void
  onRun: () => void
}

export function CaseSection({
  attempts,
  attemptCount,
  maxAttempts,
  running,
  steps,
  error,
  selectedSchemaId,
  onSelectSchema,
  onRun,
}: CaseSectionProps) {
  const { data: schemas } = useQuery({
    queryKey: ['schemas'],
    queryFn: () => schemasApi.list(),
  })

  const remaining = maxAttempts - attemptCount
  const capReached = remaining <= 0

  return (
    <Card className="space-y-4 border-border p-4 md:p-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Network className="h-4 w-4 text-primary" />
          <h2 className="text-base font-medium text-foreground">2 · Case extraction</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          In a case, the same file goes through full schema-driven LLM extraction: the model reads
          the document and pulls out structured entities and relationships according to the chosen
          schema, building a knowledge graph. Try up to {maxAttempts} times with different schemas
          to see how the schema shapes the result.
        </p>
        <div className="flex items-start gap-1.5 rounded-md border border-border bg-muted/50 p-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            This demo shows the raw extraction. A real case also runs entity resolution afterwards
            to merge duplicate entities across documents — that step is skipped here.
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={selectedSchemaId}
          onValueChange={onSelectSchema}
          disabled={running || capReached}
        >
          <SelectTrigger className="h-9 w-full md:w-64">
            <SelectValue placeholder="Select an extraction schema" />
          </SelectTrigger>
          <SelectContent>
            {(schemas ?? []).map(s => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
                {s.is_active ? ' (active)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          onClick={onRun}
          disabled={running || capReached || !selectedSchemaId}
          className="h-9 md:h-8"
        >
          {running ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Play className="mr-1.5 h-3.5 w-3.5" />
          )}
          Run extraction
        </Button>
        <Badge variant={capReached ? 'destructive' : 'secondary'}>
          {attemptCount} / {maxAttempts} attempts
        </Badge>
      </div>

      {steps.length > 0 && <StepStrip steps={steps} />}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {attempts.length === 0 && !running && (
        <p className="text-sm text-muted-foreground">No attempts yet. Pick a schema and run.</p>
      )}

      <div className="space-y-4">
        {attempts.map(attempt => (
          <AttemptCard key={attempt.id} attempt={attempt} />
        ))}
      </div>
    </Card>
  )
}

function AttemptCard({ attempt }: { attempt: PlaygroundAttempt }) {
  const nameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const e of attempt.entities) map.set(e.id, entityName(e))
    return map
  }, [attempt.entities])

  // Group entities by their type (label), sorted for stable display.
  const groups = useMemo(() => {
    const m = new Map<string, PlaygroundEntity[]>()
    for (const e of attempt.entities) {
      const list = m.get(e.label) ?? []
      list.push(e)
      m.set(e.label, list)
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [attempt.entities])

  return (
    <Card className="space-y-4 border-border bg-muted/50 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium text-foreground">Attempt {attempt.attempt_no}</h3>
        {attempt.schema_name && <Badge variant="outline">{attempt.schema_name}</Badge>}
        <Badge variant="secondary">{attempt.entities.length} entities</Badge>
        <Badge variant="secondary">{attempt.relationships.length} relationships</Badge>
      </div>

      {attempt.error && <div className="text-xs text-destructive">{attempt.error}</div>}

      {/* Entities grouped by type — research-page card grid */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground">Entities</div>
        {groups.length === 0 ? (
          <p className="text-xs text-muted-foreground">No entities extracted.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {groups.map(([type, entities]) => (
              <DemoEntityGroupCard key={type} type={type} entities={entities} />
            ))}
          </div>
        )}
      </div>

      {/* Relationships */}
      {attempt.relationships.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">Relationships</div>
          <ul className="space-y-0.5 text-xs text-foreground max-md:break-words">
            {attempt.relationships.map((r, i) => (
              <li key={i}>
                <span className="font-medium">
                  {nameById.get(r.start_node_id) ?? r.start_node_id}
                </span>
                <span className="mx-1 text-muted-foreground">—{r.type}→</span>
                <span className="font-medium">{nameById.get(r.end_node_id) ?? r.end_node_id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Graph — full width at the bottom */}
      <div className="space-y-1">
        <div className="text-xs font-medium text-muted-foreground">Graph preview</div>
        <GraphPreview
          nodes={attempt.graph.nodes}
          edges={attempt.graph.edges}
          heightClass="h-80 md:h-[28rem]"
        />
      </div>
    </Card>
  )
}
